import React from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Navbar from "../../components/LandPage/Navbar"
import Footer from "../../components/LandPage/Footer"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { PATH_MAP } from "../../configuration/PathMap"
import { SEO } from "../../seo/Seo"
import { softwareSchema, supportFaqSchema, useOrganizationSchema } from "../../seo/schema"
import Features from "../../components/LandPage/Features"
import Pricing from "../../components/LandPage/Pricing"
import Support from "../../components/LandPage/Support"
import {
  PublicAnimatedBackground,
  publicAnimatedBackgroundShellClassName,
} from "../../components/PublicAnimatedBackground/PublicAnimatedBackground"

const roles = [
  {
    icon: "👑",
    color: "from-red-500 to-orange-500",
    title: "roles.owner.title",
    subtitle: "roles.owner.subtitle",
    chips: [
      "roles.owner.chip_1",
      "roles.owner.chip_2",
      "roles.owner.chip_3",
      "roles.owner.chip_4",
      "roles.owner.chip_5",
    ],
  },
  {
    icon: "🧑‍🍳",
    color: "from-amber-500 to-yellow-400",
    title: "roles.customer.title",
    subtitle: "roles.customer.subtitle",
    chips: [
      "roles.customer.chip_1",
      "roles.customer.chip_2",
      "roles.customer.chip_3",
      "roles.customer.chip_4",
      "roles.customer.chip_5",
    ],
  },
  {
    icon: "🛵",
    color: "from-violet-600 to-fuchsia-500",
    title: "roles.courier.title",
    subtitle: "roles.courier.subtitle",
    chips: [
      "roles.courier.chip_1",
      "roles.courier.chip_2",
      "roles.courier.chip_3",
      "roles.courier.chip_4",
      "roles.courier.chip_5",
    ],
  },
]

const ownerLocalFlow = ["ownerLocal.step_1", "ownerLocal.step_2", "ownerLocal.step_3", "ownerLocal.step_4"] as const
const ownerDeliveryFlow = [
  "ownerDelivery.step_1",
  "ownerDelivery.step_2",
  "ownerDelivery.step_3",
  "ownerDelivery.step_4",
  "ownerDelivery.step_5",
] as const
const customerFlow = [
  "customerFlow.step_1",
  "customerFlow.step_2",
  "customerFlow.step_3",
  "customerFlow.step_4",
] as const
const courierFlow = ["courierFlow.step_1", "courierFlow.step_2", "courierFlow.step_3", "courierFlow.step_4"] as const

const paymentFlow = ["payment.step_1", "payment.step_2", "payment.step_3", "payment.step_4"] as const

const HowItWorksPage: React.FC = () => {
  const organizationSchema = useOrganizationSchema()

  const { t } = useTranslation("howItWorks")

  const navigate = useNavigate()

  return (
    <div
      className={`${publicAnimatedBackgroundShellClassName} h-dvh bg-white relative z-0 overflow-x-hidden overflow-y-scroll scrollbar-none`}
    >
      <PublicAnimatedBackground />

      <SEO
        title="Como funciona o PDV para restaurante com cupom fiscal e rodízio"
        description="Veja como o Mestre da Comanda organiza pedidos online, cardápio digital, comanda, PDV com emissão de cupom fiscal (NFC-e), controle de rodízio e operação de delivery em um único sistema para restaurante."
        path="/como-funciona"
        structuredData={[organizationSchema, softwareSchema, supportFaqSchema]}
      />

      <Navbar showReturn={true} />

      <section className="pt-28 pb-14 bg-white">
        <div className="max-w-[1280px] mx-auto px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.1s">
            <span className="inline-flex items-center rounded-full bg-red-100 text-red-600 text-xs font-extrabold px-3 py-1 uppercase tracking-wide">
              {t("hero.badge")}
            </span>
          </Animate>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
            <h1 className="text-4xl lg:text-6xl font-black text-slate-900 leading-tight mt-4 max-w-[880px]">
              {t("hero.title")}
            </h1>
          </Animate>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
            <p className="text-slate-600 text-base lg:text-lg mt-4 max-w-[780px]">{t("hero.subtitle")}</p>
          </Animate>

          <Animate type={AnimationTypes.slideRightIn} duration="2.2s">
            <div className="mt-8 flex flex-row flex-wrap justify-center md:justify-start gap-5">
              <button
                onClick={() => navigate(PATH_MAP.ROOT_PAGE)}
                className="cursor-pointer bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-3 rounded-2xl font-bold transition-all duration-300 hover:scale-105"
                data-interaction-description-key="interactionDescriptions:pages.landPage.howItWorksPage.button.navigatePATHMAPROOTPAGE"
              >
                {t("hero.cta_secondary")}
              </button>
            </div>
          </Animate>
        </div>
      </section>

      <div className="max-w-[1280px] mx-auto px-6">
        <div className="grid gap-6 lg:grid-cols-3">
          {roles.map((role, index) => (
            <Animate key={role.title} type={AnimationTypes.fadeInTraslate} duration={`${1 + index * 0.3}s`}>
              <article className="rounded-3xl border-2 border-orange-200 p-4 bg-white shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${role.color} text-white text-2xl flex items-center justify-center mb-2 p-5`}
                >
                  {role.icon}
                </div>

                <h3 className="text-xl font-black text-slate-900">{t(role.title)}</h3>

                <p className="text-slate-600 text-sm mt-1 mb-4">{t(role.subtitle)}</p>

                <div className="flex flex-wrap gap-2">
                  {role.chips.map((chip) => (
                    <span
                      key={chip}
                      className="text-xs font-semibold rounded-full bg-slate-100 text-slate-700 px-3 py-1"
                    >
                      {t(chip)}
                    </span>
                  ))}
                </div>
              </article>
            </Animate>
          ))}
        </div>
      </div>

      <section className="py-10 lg:py-14">
        <div className="max-w-[1180px] mx-auto px-6 grid gap-6 lg:grid-cols-2">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.1s">
            <div className="rounded-3xl border-2 border-orange-100 bg-white p-6 h-full shadow-sm">
              <h3 className="text-2xl font-black text-slate-900">🍽️ {t("ownerLocal.title")}</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">{t("ownerLocal.subtitle")}</p>
              <div className="space-y-3">
                {ownerLocalFlow.map((step, index) => (
                  <div
                    key={step}
                    className="rounded-xl border-2 border-orange-100 bg-orange-50/40 p-3 flex items-start gap-3"
                  >
                    <span className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 text-xs font-black flex items-center justify-center mt-0.5">
                      {index + 1}
                    </span>
                    <p className="text-sm text-slate-700 font-semibold">{t(step)}</p>
                  </div>
                ))}
              </div>
            </div>
          </Animate>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
            <div className="rounded-3xl border-2 border-violet-100 bg-white p-6 h-full shadow-sm">
              <h3 className="text-2xl font-black text-slate-900">📦 {t("ownerDelivery.title")}</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">{t("ownerDelivery.subtitle")}</p>
              <div className="space-y-3">
                {ownerDeliveryFlow.map((step, index) => (
                  <div
                    key={step}
                    className="rounded-xl border-2 border-violet-100 bg-red-50/40 p-3 flex items-start gap-3"
                  >
                    <span className="w-7 h-7 rounded-full bg-violet-100 text-red-700 text-xs font-black flex items-center justify-center mt-0.5">
                      {index + 1}
                    </span>
                    <p className="text-sm text-slate-700 font-semibold">{t(step)}</p>
                  </div>
                ))}
              </div>
            </div>
          </Animate>
        </div>
      </section>

      <section className="py-10 lg:py-14">
        <div className="max-w-[1180px] mx-auto px-6 grid gap-6 lg:grid-cols-2">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.1s">
            <div className="rounded-3xl border-2 border-amber-100 p-6 h-full">
              <h3 className="text-2xl font-black text-slate-900">👤 {t("customerFlow.title")}</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">{t("customerFlow.subtitle")}</p>
              <div className="space-y-3">
                {customerFlow.map((step, index) => (
                  <div key={step} className="rounded-xl bg-white border-2 border-amber-100 p-3 flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 text-xs font-black flex items-center justify-center mt-0.5">
                      {index + 1}
                    </span>
                    <p className="text-sm text-slate-700 font-semibold">{t(step)}</p>
                  </div>
                ))}
              </div>
            </div>
          </Animate>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
            <div className="rounded-3xl border-2 border-fuchsia-100 p-6 h-full">
              <h3 className="text-2xl font-black text-slate-900">🛵 {t("courierFlow.title")}</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">{t("courierFlow.subtitle")}</p>
              <div className="space-y-3">
                {courierFlow.map((step, index) => (
                  <div
                    key={step}
                    className="rounded-xl bg-white border-2 border-fuchsia-100 p-3 flex items-start gap-3"
                  >
                    <span className="w-7 h-7 rounded-full bg-fuchsia-100 text-red-700 text-xs font-black flex items-center justify-center mt-0.5">
                      {index + 1}
                    </span>
                    <p className="text-sm text-slate-700 font-semibold">{t(step)}</p>
                  </div>
                ))}
              </div>
            </div>
          </Animate>
        </div>
      </section>

      <section className="py-10 lg:py-14">
        <div className="max-w-[980px] mx-auto px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
            <h3 className="text-3xl font-black text-slate-900 text-center">💳 {t("payment.title")}</h3>
          </Animate>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
            <p className="text-center text-slate-600 mt-2 mb-6">{t("payment.subtitle")}</p>
          </Animate>

          <div className="grid gap-4 md:grid-cols-2">
            {paymentFlow.map((step, index) => (
              <Animate key={step} type={AnimationTypes.slideUpIn} duration={`${1 + index * 0.15}s`}>
                <div className="rounded-2xl border-2 border-emerald-100 bg-white p-4 flex items-start gap-3">
                  <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 text-xs font-black flex items-center justify-center mt-0.5">
                    {index + 1}
                  </span>
                  <p className="text-sm text-slate-700 font-semibold">{t(step)}</p>
                </div>
              </Animate>
            ))}
          </div>
        </div>
      </section>

      <Features />

      <section id="plans" className="py-4 mb-5">
        <Pricing showButton={true} />
      </section>

      <Support />

      <Footer />
    </div>
  )
}

export default HowItWorksPage
